<!--
  列表页统计面板

  使用:
  <lc-summary-panel>
    <lc-summary-item></lc-summary-item>
    <lc-summary-item></lc-summary-item>
  </lc-summary-panel>
 -->
<template>
  <div class="lc-summary-panel clearfix" :style="panelStyle">
    <!-- 右侧：展开/收起子统计项 -->
    <div v-if="collapsable" class="collapse-control pull-right m-l-15">
      <!-- 展开按钮 -->
      <el-button v-if="!expanded" type="text" @click="_onClickExpand">展开统计<i class="el-icon-arrow-down el-icon--right"></i></el-button>
      <!-- 收起按钮 -->
      <el-button v-else type="text" @click="_onClickCollapse">收起统计<i class="el-icon-arrow-up el-icon--right"></i></el-button>
    </div>

    <!-- 筛选 -->
    <el-dropdown v-if="!isEmptyObject(filters)" class="filter-control pull-right" @command="_onFilterCommand">
      <span class="el-dropdown-link"
        ><el-button type="text"><i class="lc-ico-filter"></i></el-button
      ></span>
      <el-dropdown-menu slot="dropdown">
        <el-dropdown-item command="all">全部</el-dropdown-item>
        <el-dropdown-item disabled command="all" divided></el-dropdown-item>
        <el-dropdown-item v-for="filter in filters" :key="filter.label" :disabled="_itemDisabled(filter.params)" :command="filter.label">{{
          filter.label
        }}</el-dropdown-item>
      </el-dropdown-menu>
    </el-dropdown>

    <!-- 左侧 -->
    <div class="lc-summary-items"><slot :expanded="expanded"></slot></div>
  </div>
</template>

<script>
const EqualWidth = 'equal-width' // 等宽
import searchUtil from '@/utils/search_util'
import arrayUtil from '@/utils/array_util'

export default {
  // 提供给子组件的属性
  provide() {
    return {
      flexValue: this.flexValue
    }
  },
  props: {
    // 宽度，如100%, 100, 100px
    width: {
      type: [Number, String],
      default: ''
    },
    // 可折叠的。需要配合配合 lc-summary-item 使用
    // 折叠后，看不到 子项
    collapsable: {
      type: Boolean,
      default: false
    },
    // Flex的值，支持： auto - 按比例 / equal-width - 等宽 / 空 - 无
    flexType: {
      type: String,
      default: ''
    },
    // 通讯总线
    // 只有定义了 filters 才需要传这个值
    bus: {
      type: Object,
      default: () => null
    },

    // 汇总的筛选（需要配搭 bus 一起使用）
    // [{ label: '可用的', params: { status: available } }]
    filters: {
      type: Array,
      default: () => []
    },
    // 是否清除其他筛选条件（需要搭配 filters 使用），如果 clearOtherParams 为 true，则筛选项选中后会清空其他项目
    // 当统计项是不跟随高级搜索变化的，建议启动 true
    clearOtherParams: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      expanded: false,
      primaryColor: '#ff0000'
    }
  },
  computed: {
    /**
     * 面板的样式
     *
     * @returns {object} 样式对象
     */
    panelStyle() {
      let widthValue = isNaN(this.width) ? this.width : `${this.width}px`
      return { width: widthValue }
    },
    /**
     * CSS flex 对应的值
     *
     * @returns {object} flex属性值
     */
    flexValue() {
      if (this.flexType === EqualWidth) return 1

      return this.flexType
    }
  },
  methods: {
    /**
     * 触发展开事件
     */
    emitExpandEvent() {
      this.$emit('expand')
    },
    /**
     * 触发收起事件
     */
    emitCollapseEvent() {
      this.$emit('collapse')
    },
    /**
     * 点击展开
     */
    _onClickExpand() {
      this.expanded = true
      this.emitExpandEvent()
    },
    /**
     * 点击收缩
     */
    _onClickCollapse() {
      this.expanded = false
      this.emitCollapseEvent()
    },
    /**
     * 筛选指令
     * 需要根据统计栏的不同类型（只保留默认筛选条件的，保留所有筛选条件的）
     * searchUtil.emitApiParamsChanged(this.bus, { coupon_definition: couponDefinitionId }, 'LcSummaryPanel')
     *
     * @param {string} command 指令
     */
    _onFilterCommand(command) {
      let params = {}

      // 全部置空（默认），因为选项之间是互斥的，所以每次都要置空其他项
      let paramKeys = arrayUtil.unique(this.filters.map(item => Object.keys(item.params)).flat())
      paramKeys.forEach(paramKey => {
        params[paramKey] = null
      })

      // 筛选指定的参数
      let filter = this.filters.find(item => item.label === command)
      if (!this.isEmptyObject(filter)) {
        params = { ...params, ...filter.params }
      }

      searchUtil.emitApiParamsChanged(this.bus, params, 'LcSummaryPanel', { clearOtherParams: this.clearOtherParams })
    },
    /**
     * 菜单禁用。如果当前筛选项的筛选条件与路由一致，则不能点击
     *
     * @param {object} filterParams 筛选项的筛选条件
     * @returns {boolean} 是否禁用
     */
    _itemDisabled(filterParams) {
      let routeParams = this.$route.query // 路由参数

      // 找出路由中是否缺失筛选项的筛选条件
      let paramKeys = Object.keys(filterParams)
      let hasMissingParam = paramKeys.some(paramKey => {
        let filterParamValue = filterParams[paramKey]
        let routerParamValue = routeParams[paramKey]
        return filterParamValue != routerParamValue
      })

      return !hasMissingParam
    }
  }
}
</script>

<style lang="scss" scoped>
/* 在根元素或者 :root 伪类中定义 CSS 变量 */
// Panel
.lc-summary-panel {
  border-radius: 4px;
  margin-bottom: 10px;
  height: 100%;
  padding: 15px 5px;

  .lc-summary-items {
    display: flex; /* 将容器设置为 flex 容器 */
    // flex-wrap: wrap; /* 当子项目溢出容器时换行 */
  }

  .lc-summary-item {
    // flex: 1; /* 子项目平均占据可用空间 */
    // flex: auto; /* 或者可以不设置 flex 属性，因为 flex 默认值为 flex: 0 1 auto; */
    // border-right: 1px solid #ade092;

    &:last-child {
      border-right: 0px;
    }
  }

  .collapse-control,
  .filter-control {
    margin-top: -10px;
  }
}
</style>
